import React, { useEffect, useMemo, useState } from 'react';
import Card from '../Card/Card';
import Const from '../../const.json';
import ChartPie from '../Charts/ChartPie/ChartPie';
import YearlyPercentDiff from '../YearlyPercentDiff/YearlyPercentDiff';
import { useAppSelector } from '../../redux/hooks';
import useApi from 'hooks/useApi';
import { GetHandlingComplaints, GetNilonLevelsDropDownList } from '../../api/apiService';
import { HandlingComplaintsEnum } from '../../enums/enums';
import WithApi from 'components/ui/WithApi/WithApi';
import { ListGroup } from 'react-bootstrap';
import { useYearFromRedux } from 'utils/utilsLogic';
import "../YearlyPercentDiff/YearlyPercentDiff.scss";
import { HandlingComplaintsResult } from 'types/HandlingComplaintsResult';
import { EntityComplaint } from 'types/EntityComplaint';
import DropDown from 'components/controls/DropDown/DropDown';

const HandlingComplaints: React.FC = () => {
  const { period, isContrast } = useAppSelector((state) => state.general);
  const { currentYearFromSession, lastYearFromSession } = useYearFromRedux();
  const { callApi, data, loading, error } = useApi<HandlingComplaintsResult>(GetHandlingComplaints);
  const [selectedIndexes, setSelectedIndexes] = useState<boolean[]>([]);
  const [optionId, setOptionId] = useState<string>(null);

  const colorDictionary = new Map<string, string>([
    [HandlingComplaintsEnum.Justified, isContrast ? '#FFC0CB' : "#1172e4"],
    [HandlingComplaintsEnum.NotJustified, isContrast ? '#ADD8E6' : "#ac6cff"],
    [HandlingComplaintsEnum.CompletionOfProcesses, isContrast ? '#FFA500' : "#72e3f4"],
    [HandlingComplaintsEnum.NoDecision, isContrast ? '#FFFF00' : "#354297"]
  ]);

  useEffect(() => {
    callApi();
  }, [period]);

  useEffect(() => {
    callApi(optionId);
  }, [optionId]);

  useEffect(() => {
    if (data?.entityComplaints) {
      setSelectedIndexes(data.entityComplaints.map(() => true));
    }
  }, [data]);

  const [pieData, labels, dataSetColors] = useMemo(() => {
    let selectedData = [];
    let pieData = [];
    let labels = [];
    let dataSetColors = [];
    if (data?.entityComplaints) {
      selectedData = data.entityComplaints.filter((d, index) => selectedIndexes[index]) ?? [];

      pieData = selectedData.map(d => d.complaintsCount);
      labels = selectedData.map(d => d.description);
      dataSetColors = selectedData.map(d => colorDictionary.get(d.id));
    }
    return [pieData, labels, dataSetColors];
  }, [selectedIndexes, data, isContrast]);

  const handleSelectOption = (optionId?: string) => setOptionId(optionId);

  return (
    <div>
      <Card header={Const.handlingComplaints}
        components={[
          <DropDown
            options={GetNilonLevelsDropDownList}
            title={Const.selectResponsibleBody}
            placeholder={Const.typeNameOfResponsibleBody}
            onSelect={handleSelectOption}
          />,
          <WithApi data={data?.entityComplaints} loading={loading} error={error}>
            <div className='d-md-flex align-items-center handingComplaints-container'>
              <div className='chart-container ps-4'>
                {data?.complaintCount !== undefined && (
                  <ChartPie complaintCount={data.complaintCount} details={{ data: pieData, dataSetColors, labels, title: Const.complaintsThatHandled }} ariaLabel={`${Const.handlingComplaints}, ${data?.complaintCount} ${Const.complaintsThatHandled}`} />
                )}
              </div>
              <div className='labels-container d-flex flex-column'>
                <div className='w-100 d-flex'>
                  <span className='col-8 custom-title'>{Const.treatmentCausesPerYear} {currentYearFromSession}</span>
                  <span className='col-4 custom-title'>{Const.complaintsRate} {lastYearFromSession}</span>
                </div>
                <ListGroup>
                  {data?.entityComplaints.map((item, index) => {
                    const circleStyle = { backgroundColor: colorDictionary.get(item.id) };
                    return (
                      <YearlyPercentDiff key={item.id}
                        item={item}
                        circleStyle={circleStyle}
                        index={index}
                        selectedIndexes={selectedIndexes}
                        setSelectedIndexes={setSelectedIndexes}
                      />)
                  })}
                </ListGroup>
              </div>
            </div>
          </WithApi>
        ]} />
    </div>
  );
};

export default HandlingComplaints;

